/** Write-side normalizer for SourceContent.kind: trim, collapse inner whitespace, lowercase. */
export function normalizeKind(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ').toLowerCase()
}

export const MAX_HIERARCHY_DEPTH = 100

/**
 * Resolve a node's parentId. `null` means "this node is a root"; `undefined`
 * means "no such node", which ends the walk without reporting a cycle.
 */
export type ParentLookup = (
  id: string,
) => Promise<string | null | undefined> | string | null | undefined

/**
 * Would re-parenting `contentId` under `newParentId` close a cycle?
 *
 * The walk climbs from the proposed parent toward the root looking for
 * `contentId`. IO is injected so this stays db-free and both writers share one
 * definition: the admin router walks on its open transaction with `FOR UPDATE`
 * on every ancestor, and the import script does the same on its own.
 *
 * Cycle safety is a property of the physical parentId graph, so callers must
 * NOT filter soft-deleted rows out of the lookup: a soft-deleted intermediate
 * node still carries a real parentId pointer, and skipping it would let a
 * write through that leaves the physical chain cyclic.
 *
 * Exhausting MAX_HIERARCHY_DEPTH means the ancestry could not be verified, so
 * it fails safe and blocks rather than silently approving.
 */
export async function wouldCreateCycle(
  contentId: string,
  newParentId: string,
  getParentId: ParentLookup,
): Promise<boolean> {
  if (contentId === newParentId) return true

  let currentId: string | null = newParentId

  for (let depth = 0; depth < MAX_HIERARCHY_DEPTH; depth++) {
    if (currentId === null) return false

    const parentId = await getParentId(currentId)
    if (parentId === undefined) return false
    if (parentId === contentId) return true

    currentId = parentId
  }

  return true
}

export type ChildRef = { type: 'container' | 'item'; id: string }

export type ChildSequenceVerdict =
  | { ok: true }
  | { ok: false; code: 'BAD_REQUEST' | 'CONFLICT'; message: string }

/**
 * Pure validation for reorderSourceContentChildren: the submitted sequence
 * must be exactly the live sibling set (both sub-containers and item
 * memberships) of the parent scope, with no duplicates. Completeness is what
 * lets `order = index` writes be safe — a stale client gets CONFLICT instead
 * of silently pushing an unseen sibling into order limbo.
 */
export function validateChildSequence({
  parentId,
  submitted,
  liveContainerIds,
  liveItemIds,
}: {
  parentId: string | null
  submitted: ChildRef[]
  liveContainerIds: string[]
  liveItemIds: string[]
}): ChildSequenceVerdict {
  const seen = new Set<string>()
  for (const child of submitted) {
    const key = `${child.type}:${child.id}`
    if (seen.has(key)) {
      return {
        ok: false,
        code: 'BAD_REQUEST',
        message: `Duplicate child entry ${key}`,
      }
    }
    seen.add(key)
  }

  if (parentId === null && submitted.some((c) => c.type === 'item')) {
    return {
      ok: false,
      code: 'BAD_REQUEST',
      message: 'Items cannot be ordered at the source root',
    }
  }

  const submittedContainers = new Set(
    submitted.filter((c) => c.type === 'container').map((c) => c.id),
  )
  const submittedItems = new Set(
    submitted.filter((c) => c.type === 'item').map((c) => c.id),
  )
  const liveContainers = new Set(liveContainerIds)
  const liveItems = new Set(liveItemIds)

  const sameSet = (a: Set<string>, b: Set<string>) =>
    a.size === b.size && [...a].every((id) => b.has(id))

  if (!sameSet(submittedContainers, liveContainers)) {
    return {
      ok: false,
      code: 'CONFLICT',
      message:
        'Submitted containers do not match the current sibling set — refresh and retry',
    }
  }
  if (!sameSet(submittedItems, liveItems)) {
    return {
      ok: false,
      code: 'CONFLICT',
      message:
        'Submitted items do not match the current sibling set — refresh and retry',
    }
  }

  return { ok: true }
}

/**
 * Display/assignment title for a container: "Read Ch 19".
 *
 * This composed value is what lands in LearningItem.title, and therefore what
 * stage() copies into CoachingAssignment.coachTitle/clientTitle. Every writer
 * of a source_content LearningItem must use this function — the registry
 * compares projected titles to decide whether a row has drifted, so two
 * different compositions would make reconciliation rewrite rows forever.
 */
export function composeSourceContentTitle(
  title: string,
  actionVerb: string | null,
): string {
  const verb = actionVerb?.trim() ?? ''
  return verb === '' ? title : `${verb} ${title}`
}
